import { useEffect, useState, useMemo } from 'react'
import { BarChart3, TrendingUp, Users, CreditCard, Mail, HardDrive, Loader2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { api } from '@/lib/api'
import { toast } from 'sonner'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  AreaChart,
  Area,
  Line,
} from 'recharts'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface DeliverabilityData {
  period_days: number
  total_sent: number
  total_delivered: number
  overall_delivery_rate: number
  bounce_rate: number
  complaint_rate: number
  blacklist_incidents: number
}

interface DeliverabilityPoint {
  date: string
  sent: number
  delivered: number
  bounced: number
}

interface BusinessSummary {
  total_accounts: number
  new_accounts: number
  active_accounts: number
  verified_accounts: number
  paying_customers: number
  mrr_cents: number
  revenue_30d_cents: number
  arpu_cents: number
  cancellations_30d: number
  churn_rate: number
  active_domains: number
  active_mailboxes: number
  emails_sent_30d: number
  storage_used_gb: number
}

interface BusinessTimeseriesPoint {
  date: string
  signups: number
  revenue_cents: number
  emails_sent: number
}

interface RecentSignup {
  email: string
  plan: string
  created_at: string
  verified: boolean
}

interface RecentPayment {
  amount_cents: number
  currency: string
  status: string
  payment_type: string
  paid_at: string | null
}

interface PlanBreakdown {
  plan: string
  count: number
}

interface BusinessData {
  period_days: number
  summary: BusinessSummary
  timeseries: BusinessTimeseriesPoint[]
  recent_signups: RecentSignup[]
  recent_payments: RecentPayment[]
  plans: PlanBreakdown[]
}

const formatCurrency = (cents: number, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

const formatDate = (iso: string | null) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const formatDateShort = (iso: string) => {
  const [, m, d] = iso.split('-').map(Number)
  return `${m}/${d}`
}

function KPICard({
  title,
  value,
  subtext,
  icon: Icon,
  accent,
}: {
  title: string
  value: string
  subtext?: string
  icon: React.ElementType
  accent?: string
}) {
  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${accent || 'text-muted-foreground'}`} />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
      </CardContent>
    </Card>
  )
}

export function Analytics() {
  const [days, setDays] = useState<string>('30')
  const [activeTab, setActiveTab] = useState('business')
  const [loading, setLoading] = useState(true)

  const [business, setBusiness] = useState<BusinessData | null>(null)
  const [deliverability, setDeliverability] = useState<DeliverabilityData | null>(null)
  const [deliverabilitySeries, setDeliverabilitySeries] = useState<DeliverabilityPoint[]>([])

  const periodDays = useMemo(() => Number(days), [days])

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true)
      try {
        const [businessRes, deliverabilityRes, timeseriesRes] = await Promise.all([
          api.get(`/admin/analytics/business?days=${periodDays}`),
          api.get('/deliverability/admin/analytics/deliverability'),
          api.get('/deliverability/admin/analytics/timeseries'),
        ])
        setBusiness(businessRes.data)
        setDeliverability(deliverabilityRes.data)
        setDeliverabilitySeries(timeseriesRes.data.series || [])
      } catch {
        toast.error('Failed to load analytics')
      } finally {
        setLoading(false)
      }
    }
    fetchAnalytics()
  }, [periodDays])

  const deliveryRatePct = deliverability ? Math.round(deliverability.overall_delivery_rate * 100) : 0
  const bounceRatePct = deliverability ? Math.round(deliverability.bounce_rate * 100) : 0
  const complaintRatePct = deliverability ? Math.round(deliverability.complaint_rate * 100) : 0

  const revenueChartData = useMemo(
    () =>
      business?.timeseries.map((d) => ({
        date: d.date,
        revenue: d.revenue_cents / 100,
      })) || [],
    [business]
  )

  const emailsChartData = useMemo(
    () =>
      business?.timeseries.map((d) => ({
        date: d.date,
        emails: d.emails_sent,
      })) || [],
    [business]
  )

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-primary" />
            Analytics
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Business and platform analytics</p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Select range" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="business">Business</TabsTrigger>
            <TabsTrigger value="deliverability">Deliverability</TabsTrigger>
          </TabsList>

          <TabsContent value="business" className="space-y-6 mt-6">
            {/* Growth & Revenue */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KPICard
                title="MRR"
                value={formatCurrency(business?.summary.mrr_cents || 0)}
                subtext={`${business?.summary.paying_customers || 0} paying customers`}
                icon={TrendingUp}
                accent="text-emerald-500"
              />
              <KPICard
                title={`Revenue (${business?.period_days || 30}d)`}
                value={formatCurrency(business?.summary.revenue_30d_cents || 0)}
                subtext={`ARPU ${formatCurrency(business?.summary.arpu_cents || 0)}`}
                icon={CreditCard}
                accent="text-blue-500"
              />
              <KPICard
                title="New Signups"
                value={(business?.summary.new_accounts || 0).toLocaleString()}
                subtext={`${business?.summary.total_accounts || 0} total accounts`}
                icon={Users}
                accent="text-purple-500"
              />
              <KPICard
                title="Active Accounts"
                value={(business?.summary.active_accounts || 0).toLocaleString()}
                subtext={`${business?.summary.verified_accounts || 0} verified emails`}
                icon={Users}
                accent="text-amber-500"
              />
            </div>

            {/* Platform Usage */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KPICard
                title="Emails Sent"
                value={(business?.summary.emails_sent_30d || 0).toLocaleString()}
                subtext="Last 30 days"
                icon={Mail}
                accent="text-indigo-500"
              />
              <KPICard
                title="Active Domains"
                value={(business?.summary.active_domains || 0).toLocaleString()}
                subtext="Verified and active"
                icon={Mail}
                accent="text-cyan-500"
              />
              <KPICard
                title="Active Mailboxes"
                value={(business?.summary.active_mailboxes || 0).toLocaleString()}
                subtext="Across all accounts"
                icon={Mail}
                accent="text-pink-500"
              />
              <KPICard
                title="Storage Used"
                value={`${business?.summary.storage_used_gb || 0} GB`}
                subtext={`${business?.summary.cancellations_30d || 0} cancellations`}
                icon={HardDrive}
                accent="text-orange-500"
              />
            </div>

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Revenue & Signups</CardTitle>
                </CardHeader>
                <CardContent>
                  {revenueChartData.length === 0 ? (
                    <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
                      No data yet.
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={business?.timeseries} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={formatDateShort} />
                        <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                        <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12 }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '8px' }}
                          labelStyle={{ color: '#e0e0e0' }}
                          labelFormatter={(label: string) => formatDate(label)}
                          formatter={(value: number, name: string) => {
                            if (name === 'Revenue') return [formatCurrency(value * 100), name]
                            return [value, name]
                          }}
                        />
                        <Legend />
                        <Area
                          yAxisId="left"
                          type="monotone"
                          dataKey="revenue_cents"
                          name="Revenue"
                          stroke="#3b82f6"
                          fillOpacity={1}
                          fill="url(#colorRevenue)"
                          strokeWidth={2}
                        />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="signups"
                          name="Signups"
                          stroke="#8b5cf6"
                          strokeWidth={2}
                          dot={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Emails Sent</CardTitle>
                </CardHeader>
                <CardContent>
                  {emailsChartData.length === 0 ? (
                    <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
                      No email data yet.
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={emailsChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={formatDateShort} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '8px' }}
                          labelStyle={{ color: '#e0e0e0' }}
                          labelFormatter={(label: string) => formatDate(label)}
                        />
                        <Bar dataKey="emails" fill="#10b981" name="Emails sent" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-sm font-medium">Recent Signups</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Verified</TableHead>
                        <TableHead className="text-right">Joined</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(business?.recent_signups || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-muted-foreground">
                            No signups yet.
                          </TableCell>
                        </TableRow>
                      ) : (
                        business?.recent_signups.map((signup) => (
                          <TableRow key={`${signup.email}-${signup.created_at}`}>
                            <TableCell className="font-medium">{signup.email}</TableCell>
                            <TableCell className="capitalize">{signup.plan}</TableCell>
                            <TableCell>
                              <Badge variant={signup.verified ? 'default' : 'secondary'}>
                                {signup.verified ? 'Yes' : 'No'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right text-muted-foreground">
                              {formatDate(signup.created_at)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium">Plans</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Plan</TableHead>
                          <TableHead className="text-right">Active</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(business?.plans || []).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} className="text-center text-muted-foreground">
                              No active subscriptions.
                            </TableCell>
                          </TableRow>
                        ) : (
                          business?.plans.map((p) => (
                            <TableRow key={p.plan}>
                              <TableCell className="capitalize">{p.plan}</TableCell>
                              <TableCell className="text-right font-medium">{p.count}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm font-medium">Recent Payments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Amount</TableHead>
                          <TableHead className="text-right">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(business?.recent_payments || []).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={2} className="text-center text-muted-foreground">
                              No payments yet.
                            </TableCell>
                          </TableRow>
                        ) : (
                          business?.recent_payments.map((payment, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{formatCurrency(payment.amount_cents, payment.currency)}</TableCell>
                              <TableCell className="text-right">
                                <Badge variant={payment.status === 'succeeded' ? 'default' : 'destructive'}>
                                  {payment.status}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="deliverability" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <KPICard title="Emails Sent (30d)" value={(deliverability?.total_sent ?? 0).toLocaleString()} icon={Mail} />
              <KPICard
                title="Delivery Rate"
                value={`${deliveryRatePct}%`}
                icon={TrendingUp}
                accent={deliveryRatePct >= 95 ? 'text-emerald-500' : deliveryRatePct >= 90 ? 'text-amber-500' : 'text-red-500'}
              />
              <KPICard
                title="Bounce Rate"
                value={`${bounceRatePct}%`}
                icon={TrendingUp}
                accent={bounceRatePct <= 2 ? 'text-emerald-500' : bounceRatePct <= 5 ? 'text-amber-500' : 'text-red-500'}
              />
              <KPICard
                title="Complaint Rate"
                value={`${complaintRatePct}%`}
                icon={TrendingUp}
                accent={complaintRatePct <= 0.1 ? 'text-emerald-500' : complaintRatePct <= 0.3 ? 'text-amber-500' : 'text-red-500'}
              />
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium">Daily Volume (last 30 days)</CardTitle>
              </CardHeader>
              <CardContent>
                {deliverabilitySeries.length === 0 ? (
                  <div className="h-64 flex items-center justify-center text-muted-foreground text-sm">
                    No email data yet. Data will appear once emails are sent.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={deliverabilitySeries} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} tickFormatter={formatDateShort} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '8px' }}
                        labelStyle={{ color: '#e0e0e0' }}
                        labelFormatter={(label: string) => formatDate(label)}
                      />
                      <Legend />
                      <Bar dataKey="sent" fill="#8b5cf6" name="Sent" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="delivered" fill="#10b981" name="Delivered" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="bounced" fill="#ef4444" name="Bounced" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  )
}
